import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { normalizedAddress } from 'services/apollo/queries/normalizedAddress';
import {
  NormalizedAddress,
  NormalizedAddressVariables,
} from 'types/generated/NormalizedAddress';
import {
  AddressStatus,
  AddressClassification,
} from 'types/generated/globalTypes';
import { Address } from 'types';
import { AddressValidationResult, ValidatedAddress } from './types';

/**
 * Maps GraphQL address status to validation result
 */
function mapStatusToValidation(
  status: AddressStatus | null,
  classification: AddressClassification
): AddressValidationResult {
  const isCommercial = classification === AddressClassification.COMMERCIAL;

  switch (status) {
    case AddressStatus.VALID:
      return {
        isValid: true,
        requiresUnit: false,
        isCommercial,
        isVacant: false,
        footnotes: 'VALID',
      };

    case AddressStatus.APT_NEEDED:
      return {
        isValid: false,
        requiresUnit: true,
        isCommercial,
        isVacant: false,
        footnotes: 'APT_NEEDED',
        errorMessage:
          'This address is a multi-unit building. Please enter your apartment or suite number.',
      };

    case AddressStatus.INVALID:
      return {
        isValid: false,
        requiresUnit: false,
        isCommercial,
        isVacant: false,
        footnotes: 'INVALID',
        errorMessage:
          'We could not verify this address. Please check that it is correct.',
      };

    default:
      // null status
      return {
        isValid: false,
        requiresUnit: false,
        isCommercial,
        isVacant: false,
        footnotes: '',
        errorMessage: 'Unable to validate address status.',
      };
  }
}

export function useAddressValidation() {
  const [executeQuery, { loading, error }] = useLazyQuery<
    NormalizedAddress,
    NormalizedAddressVariables
  >(normalizedAddress, {
    fetchPolicy: 'network-only', // Always fetch fresh validation
  });

  const validateAddress = useCallback(
    async (address: Address): Promise<ValidatedAddress> => {
      try {
        const result = await executeQuery({
          variables: {
            input: {
              street: address.street,
              unit: address.unit || null,
              city: address.city,
              state: address.state,
              postalCode: address.postalCode,
            },
          },
        });

        // Handle GraphQL errors
        if (result.error) {
          return {
            ...address,
            validation: {
              isValid: false,
              requiresUnit: false,
              isCommercial: false,
              isVacant: false,
              footnotes: '',
              errorMessage: 'Unable to validate address. Please try again.',
            },
          };
        }

        // Extract normalized address from nested response
        const normalizedAddr =
          result.data?.normalizedAddress?.normalizedAddress;

        // Handle null response (address not found)
        if (!normalizedAddr) {
          return {
            ...address,
            validation: {
              isValid: false,
              requiresUnit: false,
              isCommercial: false,
              isVacant: false,
              footnotes: '',
              errorMessage:
                'Address not found. Please check that it is correct.',
            },
          };
        }

        // Map GraphQL status to validation result
        const validation = mapStatusToValidation(
          normalizedAddr.status,
          normalizedAddr.classification
        );

        // For VALID or APT_NEEDED, return normalized address
        // For INVALID, return original address (let user keep their input)
        if (
          normalizedAddr.status === AddressStatus.VALID ||
          normalizedAddr.status === AddressStatus.APT_NEEDED
        ) {
          return {
            street: normalizedAddr.street,
            unit: normalizedAddr.unit || '',
            city: normalizedAddr.city,
            state: normalizedAddr.state,
            postalCode: normalizedAddr.postalCode,
            validation,
          };
        } else {
          // INVALID or unknown status - return original address with validation result
          return {
            ...address,
            validation,
          };
        }
      } catch (err) {
        // Handle unexpected errors
        return {
          ...address,
          validation: {
            isValid: false,
            requiresUnit: false,
            isCommercial: false,
            isVacant: false,
            footnotes: '',
            errorMessage: 'Unable to validate address. Please try again.',
          },
        };
      }
    },
    [executeQuery]
  );

  return {
    validateAddress,
    isValidating: loading,
    error,
  };
}
